import { zodResolver } from '@hookform/resolvers/zod'
import {
  ApiError,
  type ApplyImageToVariationsResult,
  type BulkUpdateProductsResult,
  type CreateProductInput,
  createProductSchema,
  formatMoney,
  type GenerateSkuResponse,
  isUnitTracked,
  MAX_GALLERY_IMAGES,
  PRODUCT_SALES_CHANNELS,
  type ProductSalesChannel,
  type ProductAttributeDefinition,
  type ProductCategory,
  PRODUCT_TYPES,
  type ProductType,
  queryKeys,
  STOCKLESS_PRODUCT_TYPES,
  type TaxCategory,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  useToast,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  ArrowRight,
  ArrowRightLeft,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  GitMerge,
  Images,
  MoreVertical,
  Package,
  PackagePlus,
  Plus,
  Printer,
  Search,
  ShoppingCart,
  Star,
  Tag,
  Trash2,
  Unlink,
  Upload,
  Warehouse,
  X,
} from 'lucide-react'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { AiImageButton, imageIdFromUrl } from '../components/ai-assist.js'
import { Page, PageHeader } from '../components/app-shell.js'
import {
  CompatibleDevicesEditor,
  useDeviceSuggestions,
} from '../components/compatible-devices-editor.js'
import { ProductImportDialog } from '../components/csv-import-dialog.js'
import { AttributePricesDialog } from '../components/attribute-prices-dialog.js'
import { BulkActionBar, DataTable } from '../components/data-table.js'
import { ImageLibraryDialog } from '../components/image-library-grid.js'
import { PlanBillingSection } from '../components/plan-billing-section.js'
import { RichTextEditor } from '../components/rich-text-editor.js'
import { api, apiBaseUrl } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface ProductRow {
  id: string
  sku: string
  name: string
  type: ProductType
  giftCardKind: 'digital' | 'physical' | null
  priceCents: number
  costCents: number | null
  salePriceCents: number | null
  description: string | null
  category: string | null
  taxCategoryId: string | null
  brand: string | null
  imageUrl: string | null
  /** Extra pictures after the main one, in display order. */
  galleryImageUrls: string[]
  /** Free-text device tags this product fits. */
  compatibleDevices: string[]
  manufacturerSku: string | null
  sourceUrl: string | null
  reorderPoint: number | null
  sortOrder: number
  isActive: boolean
  isInternal: boolean
  /** Where it is offered: the website, the kiosk side (tablet, vending machines), or both. */
  salesChannel: ProductSalesChannel
  skuLocked: boolean
  isArchived: boolean
  availableCount: number
  attributes: Record<string, string | number | boolean>
  parentId: string | null
  /** Active and inactive variations under this listing — empty on a
   *  variation itself, and on a listing with none. Only ever populated when
   *  the list was fetched with `grouped: true`. */
  variations: ProductRow[]
}

/** A variation row, flattened into the table under its listing when
 *  expanded — carries its listing's own fields (it is a `ProductRow` too)
 *  plus this flag, which the columns use to render it as a child. */
type TableRow = ProductRow & { isVariation?: boolean; depth?: number }

/** The options actually sold beneath a node: its descendants that have no
 *  variations of their own (a listing or sub-listing is never sold itself). */
function leafDescendants(p: ProductRow): ProductRow[] {
  return p.variations.flatMap((v) => (v.variations.length > 0 ? leafDescendants(v) : [v]))
}

/** Everything beneath a node, at every level — variations and sub-listings. */
function allDescendants(p: ProductRow): ProductRow[] {
  return p.variations.flatMap((v) => [v, ...allDescendants(v)])
}

/** How many levels sit below this node: 0 for a leaf, 1 with variations,
 *  2 with variations that have variations of their own. */
function treeHeight(p: ProductRow): number {
  return p.variations.length === 0 ? 0 : 1 + Math.max(...p.variations.map(treeHeight))
}

/** Products nest three levels (listing > sub-listing > variation); the
 *  products_variant_rules trigger and the API enforce it, this only gates
 *  which actions the table offers. */
const MAX_PRODUCT_LEVELS = 3

/** Indent per nesting depth — literal class names so Tailwind keeps them. */
const DEPTH_INDENT = ['', 'ps-8', 'ps-16']

const TYPE_LABEL: Record<ProductType, string> = {
  serialized: 'Tracked by IMEI',
  stock: 'Counted stock',
  plan: 'Service plan',
  service: 'Service',
  sim: 'Tracked by ICCID',
  gift_card: 'Gift card',
  digital_license: 'Digital license',
}

/** The price to show for a row: a listing with active variations shows
 *  their range (sale prices on variations don't factor in, same as the
 *  storefront's "From $X"), otherwise the row's own price with its sale
 *  price struck through when set. Shared by the Price column and the
 *  quick view. */
function renderPrice(p: TableRow) {
  if (p.variations.length > 0) {
    const active = leafDescendants(p).filter((v) => v.isActive)
    if (active.length > 0) {
      const prices = active.map((v) => v.priceCents)
      const min = Math.min(...prices)
      const max = Math.max(...prices)
      return (
        <span className="tabular">
          {min === max ? formatMoney(min) : `${formatMoney(min)}–${formatMoney(max)}`}
        </span>
      )
    }
    // All variations inactive — fall back to the listing's own stored
    // price below, same as a listing with no variations at all.
  }
  if (p.salePriceCents === null) {
    return <span className="tabular">{formatMoney(p.priceCents)}</span>
  }
  return (
    <span className="tabular">
      <span className="text-foreground-subtle line-through">{formatMoney(p.priceCents)}</span>{' '}
      <span className="text-danger">{formatMoney(p.salePriceCents)}</span>
    </span>
  )
}

export function ProductsPage() {
  const { can } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [deviceFilter, setDeviceFilter] = useState('')
  const deviceSuggestions = useDeviceSuggestions()
  // Opens on Active only (t1240); Inactive, Archived and All stay in the menu.
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'archived'>(
    'active',
  )
  const [sort, setSort] = useState<{ column: string; order: 'asc' | 'desc' }>({
    column: 'createdAt',
    order: 'desc',
  })
  const [creating, setCreating] = useState(false)
  // Bumped each time the plain "New product" flow opens, so CreateProductDialog
  // remounts and re-reads whatever draft is on disk at that moment. Only bumped
  // on open (not close) so the dialog's close animation isn't cut short.
  const [draftEpoch, setDraftEpoch] = useState(0)
  const [importing, setImporting] = useState(false)
  const [editing, setEditing] = useState<ProductRow | null>(null)
  const [quickView, setQuickView] = useState<TableRow | null>(null)
  const [addingVariationFor, setAddingVariationFor] = useState<ProductRow | null>(null)
  const [convertingProduct, setConvertingProduct] = useState<ProductRow | null>(null)
  const [pricingAttributesFor, setPricingAttributesFor] = useState<ProductRow | null>(null)
  const [detachingProduct, setDetachingProduct] = useState<TableRow | null>(null)
  const [applyingImageFor, setApplyingImageFor] = useState<ProductRow | null>(null)
  const [applyImageMode, setApplyImageMode] = useState<'all' | 'missing'>('missing')
  const [duplicating, setDuplicating] = useState<ProductRow | null>(null)
  const [variationSource, setVariationSource] = useState<ProductRow | null>(null)
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set())
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkEditing, setBulkEditing] = useState(false)
  const [confirmingBulkDeactivate, setConfirmingBulkDeactivate] = useState(false)
  const [confirmingBulkArchive, setConfirmingBulkArchive] = useState(false)
  const [deleting, setDeleting] = useState<ProductRow | null>(null)

  function toggleExpanded(productId: string) {
    setExpandedProducts((current) => {
      const next = new Set(current)
      if (next.has(productId)) next.delete(productId)
      else next.add(productId)
      return next
    })
  }

  // A variation duplicates into a new sibling under the same listing — its
  // parent is always already loaded, since a variation row only ever renders
  // under its (expanded) listing.
  const findLoaded = (id: string | null | undefined): ProductRow | undefined => {
    const search = (nodes: ProductRow[]): ProductRow | undefined => {
      for (const n of nodes) {
        if (n.id === id) return n
        const hit = search(n.variations)
        if (hit) return hit
      }
      return undefined
    }
    return id ? search(items) : undefined
  }

  function duplicateProduct(p: TableRow) {
    if (p.parentId) {
      const parent = findLoaded(p.parentId)
      if (!parent) return
      setVariationSource(p)
      setAddingVariationFor(parent)
    } else {
      setDuplicating(p)
    }
  }

  const filters = {
    q: search || undefined,
    type: typeFilter === 'all' ? undefined : typeFilter,
    category: categoryFilter === 'all' ? undefined : categoryFilter,
    device: deviceFilter.trim() || undefined,
    ...(statusFilter === 'active'
      ? { isActive: true }
      : statusFilter === 'inactive'
        ? { isActive: false, isArchived: false }
        : statusFilter === 'archived'
          ? { isArchived: true }
          : {}),
    sort: sort.column,
    order: sort.order,
    pageSize: 50,
    // Variations get their own listing card here, not a row of their own —
    // this is the one screen that groups them; every other picker still
    // wants the flat list.
    grouped: true,
  }

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.products.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: ProductRow[]; total: number; page: number; pageSize: number }>('/products', {
        query: { ...filters, page: pageParam },
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  const items = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  const { data: categories } = useQuery({
    queryKey: queryKeys.products.categories(),
    queryFn: () => api.get<ProductCategory[]>('/categories'),
  })

  // Variations ride along on their listing's row already, so expanding one
  // is just inserting its children after it — no extra fetch, and lazy
  // loading (server-side, over listings only) is unaffected.
  const rows = useMemo<TableRow[]>(
    () =>
      items.flatMap((root) => {
        const walk = (p: ProductRow, depth: number): TableRow[] => [
          { ...p, isVariation: depth > 0, depth },
          ...(expandedProducts.has(p.id) ? p.variations.flatMap((v) => walk(v, depth + 1)) : []),
        ]
        return walk(root, 0)
      }),
    [items, expandedProducts],
  )

  // Selection only ever means rows you can currently see — filters, paging,
  // or collapsing a listing drop the ids that left the table, so the count
  // in the bulk bar never outlives what it was counted against.
  useEffect(() => {
    setSelectedIds((prev) => {
      const visible = new Set(rows.map((r) => r.id))
      const next = new Set([...prev].filter((id) => visible.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [rows])

  const toggleActive = useMutation({
    mutationFn: (input: { id: string; isActive: boolean }) =>
      api.patch(`/products/${input.id}`, { isActive: input.isActive }),
    // Optimistic: a toggle is low-risk and reversible, and waiting on a round
    // trip for a switch feels broken.
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.products.list(filters) })
      const previous = queryClient.getQueryData(queryKeys.products.list(filters))
      queryClient.setQueryData(
        queryKeys.products.list(filters),
        // The cache under this key now holds every loaded chunk (useInfiniteQuery's
        // `{ pages, pageParams }`), not one flat page — the toggled row could be on
        // any of them.
        (old: { pages: { items: ProductRow[] }[] } | undefined) =>
          old
            ? {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  items: page.items.map((p) =>
                    p.id === input.id ? { ...p, isActive: input.isActive } : p,
                  ),
                })),
              }
            : old,
      )
      return { previous }
    },
    onError: (error, _input, context) => {
      queryClient.setQueryData(queryKeys.products.list(filters), context?.previous)
      toast.error('Could not update', error instanceof ApiError ? error.message : undefined)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.products.all() }),
  })

  // Not optimistic, unlike toggleActive above: archiving is a bigger decision
  // (it hides a product from the Inactive list too) and the 409s the API can
  // send back — activating while archived, unarchiving under an archived
  // parent — are worth showing rather than papering over with a rollback.
  const setArchived = useMutation({
    mutationFn: (input: { id: string; isArchived: boolean }) =>
      api.patch(`/products/${input.id}`, { isArchived: input.isArchived }),
    onSuccess: async (_result, input) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      if (input.isArchived) {
        toast.success('Product archived')
      } else {
        toast.toast({
          tone: 'success',
          title: 'Product unarchived',
          description: 'It is still inactive — use the switch to put it back on sale.',
        })
      }
    },
    onError: (error) =>
      toast.error('Could not update', error instanceof ApiError ? error.message : undefined),
  })

  const detach = useMutation({
    mutationFn: (product: TableRow) => api.patch(`/products/${product.id}`, { parentId: null }),
    onSuccess: async (_result, product) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      toast.success(`${product.name} is now a standalone product`)
      setDetachingProduct(null)
    },
    onError: (error) =>
      toast.error(
        'Could not detach the product',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const applyImageToVariations = useMutation({
    mutationFn: (input: { id: string; mode: 'all' | 'missing' }) =>
      api.post<ApplyImageToVariationsResult>(`/products/${input.id}/apply-image-to-variations`, {
        mode: input.mode,
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      const n = result.updated.length
      toast.success(
        n === 0 ? 'No variations needed updating' : `${n} variation${n === 1 ? '' : 's'} updated`,
      )
      setApplyingImageFor(null)
    },
    onError: (error) =>
      toast.error(
        'Could not apply the image',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`/products/${id}`),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      toast.toast({
        tone: 'success',
        title: result.deleted ? 'Product deleted' : 'Product archived instead',
        description: result.deleted
          ? undefined
          : 'It has order or inventory history, which is kept intact.',
      })
      setDeleting(null)
    },
    onError: (error) =>
      toast.error(
        'Could not delete the product',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const bulkUpdate = useMutation({
    mutationFn: (set: {
      priceCents?: number
      category?: string | null
      taxCategoryId?: string | null
      isActive?: boolean
      isArchived?: boolean
      salesChannel?: ProductSalesChannel
    }) =>
      api.post<BulkUpdateProductsResult>('/products/bulk', {
        productIds: [...selectedIds],
        set,
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      setSelectedIds(new Set())
      setBulkEditing(false)
      setConfirmingBulkDeactivate(false)
      setConfirmingBulkArchive(false)
      if (result.failed.length === 0) {
        toast.success(
          `${result.updated.length} product${result.updated.length === 1 ? '' : 's'} updated`,
        )
      } else {
        toast.toast({
          tone: 'warning',
          title: `${result.updated.length} updated, ${result.failed.length} could not be`,
          description: result.failed[0]?.reason,
        })
      }
    },
    onError: (error) =>
      toast.error(
        'Could not update those products',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const columns = useMemo<ColumnDef<TableRow, unknown>[]>(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        meta: { sortKey: 'name' },
        cell: ({ row }) => {
          const p = row.original
          const hasVariations = p.variations.length > 0
          const expanded = expandedProducts.has(p.id)
          const attrSummary =
            p.isVariation && !hasVariations
              ? Object.entries(p.attributes)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(' · ')
              : ''

          return (
            <div className={`flex items-center gap-3 min-w-0 ${DEPTH_INDENT[p.depth ?? 0] ?? ''}`}>
              {hasVariations ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleExpanded(p.id)
                  }}
                  className="shrink-0 text-foreground-muted hover:text-foreground"
                  aria-label={expanded ? `Collapse ${p.name}` : `Expand ${p.name}`}
                >
                  {expanded ? (
                    <ChevronDown className="size-3.5" />
                  ) : (
                    <ChevronRight className="size-3.5" />
                  )}
                </button>
              ) : null}
              <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md border border-border bg-surface-sunken">
                {p.imageUrl ? (
                  <img
                    src={p.imageUrl}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                ) : null}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p
                    className={`truncate font-medium ${p.isActive ? '' : 'text-foreground-muted'}`}
                  >
                    {p.name}
                  </p>
                  {p.isArchived ? (
                    <Badge tone="neutral">Archived</Badge>
                  ) : p.isActive ? null : (
                    <Badge tone="neutral">Inactive</Badge>
                  )}
                  {p.isInternal ? <Badge tone="warning">Internal</Badge> : null}
                  {p.salesChannel !== 'all' ? (
                    <Badge tone="neutral">{SALES_CHANNEL_BADGE[p.salesChannel]}</Badge>
                  ) : null}
                  {p.skuLocked ? <Badge tone="neutral">SKU locked</Badge> : null}
                </div>
                <p className="font-identifier text-xs text-foreground-muted">
                  {p.sku}
                  {hasVariations
                    ? ` · ${p.variations.length} variation${p.variations.length === 1 ? '' : 's'}`
                    : attrSummary
                      ? ` · ${attrSummary}`
                      : ''}
                </p>
              </div>
            </div>
          )
        },
      },
      {
        header: 'Type',
        accessorKey: 'type',
        meta: { sortKey: 'type' },
        cell: ({ row }) => (
          <Badge tone={isUnitTracked(row.original.type) ? 'primary' : 'neutral'}>
            {TYPE_LABEL[row.original.type]}
          </Badge>
        ),
      },
      {
        header: 'Category',
        accessorKey: 'category',
        meta: { sortKey: 'category' },
        cell: ({ row }) =>
          row.original.category ? (
            <span>{row.original.category}</span>
          ) : (
            <span className="text-foreground-subtle">—</span>
          ),
      },
      {
        header: 'Order',
        accessorKey: 'sortOrder',
        meta: { sortKey: 'sortOrder' },
        cell: ({ row }) => (
          <span className="tabular-nums text-foreground-muted">{row.original.sortOrder}</span>
        ),
      },
      {
        header: 'Price',
        accessorKey: 'priceCents',
        // Sorting still orders by the listing's own stored price, not the
        // range below — there's no single number to sort a range by.
        meta: { sortKey: 'priceCents' },
        cell: ({ row }) => renderPrice(row.original),
      },
      {
        header: 'Available',
        accessorKey: 'availableCount',
        meta: { sortKey: 'availableCount' },
        cell: ({ row }) => {
          const p = row.original
          if (STOCKLESS_PRODUCT_TYPES.includes(p.type)) {
            return <span className="text-foreground-subtle">—</span>
          }
          if (p.variations.length === 0) {
            return (
              <span className={`tabular ${p.availableCount === 0 ? 'text-danger' : ''}`}>
                {p.availableCount}
              </span>
            )
          }
          // A listing itself is never sold once it has variations — this is
          // the total across them, with any stock still sitting on the
          // listing itself (left over from before it grew variations) called
          // out so it doesn't get forgotten there.
          const variationTotal = leafDescendants(p).reduce((n, v) => n + v.availableCount, 0)
          return (
            <span className={`tabular ${variationTotal === 0 ? 'text-danger' : ''}`}>
              {variationTotal}
              {p.availableCount > 0 ? (
                <span className="ms-1 text-2xs text-foreground-muted">
                  (+{p.availableCount} on listing)
                </span>
              ) : null}
            </span>
          )
        },
      },
      {
        header: '',
        id: 'edit',
        cell: ({ row }) => {
          const p = row.original
          const canWrite = can('catalog:write')
          const printLabel = () =>
            navigate({
              to: '/labels',
              search: { sku: p.sku, name: p.name, priceCents: p.salePriceCents ?? p.priceCents },
            })
          return (
            <div className="flex items-center justify-end gap-1.5">
              {canWrite && (p.depth ?? 0) < MAX_PRODUCT_LEVELS - 1 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    setAddingVariationFor(p)
                  }}
                >
                  <Plus /> Variation
                </Button>
              ) : null}
              {canWrite ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    setEditing(p)
                  }}
                >
                  Edit
                </Button>
              ) : null}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`More actions for ${p.name}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {can('orders:write') &&
                  p.isActive &&
                  !p.isArchived &&
                  p.variations.length === 0 &&
                  (!(isUnitTracked(p.type) || p.type === 'stock') || p.availableCount > 0) ? (
                    <DropdownMenuItem
                      onSelect={() =>
                        navigate({ to: '/orders/new', search: { addProductId: p.id } })
                      }
                    >
                      <ShoppingCart /> Sell
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuItem onSelect={printLabel}>
                    <Printer /> Print label
                  </DropdownMenuItem>
                  {p.type === 'stock' && can('inventory:read') ? (
                    <DropdownMenuItem
                      onSelect={() =>
                        navigate({ to: '/stock', search: { productId: p.id, sku: p.sku } })
                      }
                    >
                      <Warehouse /> View stock levels
                    </DropdownMenuItem>
                  ) : null}
                  {p.type === 'stock' && can('inventory:adjust') ? (
                    <DropdownMenuItem
                      onSelect={() =>
                        navigate({
                          to: '/stock',
                          search: { receive: true, productId: p.id, sku: p.sku },
                        })
                      }
                    >
                      <PackagePlus /> Receive stock
                    </DropdownMenuItem>
                  ) : null}
                  {isUnitTracked(p.type) && can('inventory:write') ? (
                    <DropdownMenuItem
                      onSelect={() => navigate({ to: '/intake', search: { productId: p.id } })}
                    >
                      <PackagePlus /> {p.type === 'sim'
                        ? 'Receive SIMs'
                        : p.type === 'digital_license'
                          ? 'Receive licenses'
                          : 'Receive devices'}
                    </DropdownMenuItem>
                  ) : null}
                  {canWrite ? (
                    <DropdownMenuItem onSelect={() => duplicateProduct(p)}>
                      <Copy /> Duplicate
                    </DropdownMenuItem>
                  ) : null}
                  {canWrite && p.variations.length > 0 && p.imageUrl ? (
                    <DropdownMenuItem
                      onSelect={() => {
                        setApplyImageMode(
                          allDescendants(p).some((v) => !v.imageUrl) ? 'missing' : 'all',
                        )
                        setApplyingImageFor(p)
                      }}
                    >
                      <Images /> Apply image to variations…
                    </DropdownMenuItem>
                  ) : null}
                  {canWrite && isUnitTracked(p.type) && p.variations.length === 0 ? (
                    <DropdownMenuItem onSelect={() => setPricingAttributesFor(p)}>
                      <Tag /> Attribute prices…
                    </DropdownMenuItem>
                  ) : null}
                  {canWrite && !p.isVariation && treeHeight(p) < MAX_PRODUCT_LEVELS - 1 ? (
                    <DropdownMenuItem onSelect={() => setConvertingProduct(p)}>
                      <GitMerge /> Make a variation of…
                    </DropdownMenuItem>
                  ) : null}
                  {canWrite && p.isVariation ? (
                    <DropdownMenuItem onSelect={() => setConvertingProduct(p)}>
                      <ArrowRightLeft /> Move to another product…
                    </DropdownMenuItem>
                  ) : null}
                  {canWrite && p.isVariation ? (
                    <DropdownMenuItem onSelect={() => setDetachingProduct(p)}>
                      <Unlink /> Detach to standalone…
                    </DropdownMenuItem>
                  ) : null}
                  {canWrite ? (
                    p.isArchived ? (
                      <DropdownMenuItem
                        onSelect={() => setArchived.mutate({ id: p.id, isArchived: false })}
                      >
                        <ArchiveRestore /> Unarchive
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        onSelect={() => setArchived.mutate({ id: p.id, isArchived: true })}
                      >
                        <Archive /> Archive
                      </DropdownMenuItem>
                    )
                  ) : null}
                  {canWrite ? (
                    <DropdownMenuItem destructive onSelect={() => setDeleting(p)}>
                      <Trash2 /> Delete permanently…
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        },
      },
      {
        header: 'Active',
        accessorKey: 'isActive',
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={row.original.isActive}
              disabled={!can('catalog:write') || row.original.isArchived}
              onCheckedChange={(checked) =>
                toggleActive.mutate({ id: row.original.id, isActive: checked })
              }
              aria-label={`${row.original.isActive ? 'Deactivate' : 'Activate'} ${row.original.name}`}
            />
          </div>
        ),
      },
    ],
    [
      can,
      navigate,
      toggleActive,
      setArchived,
      setEditing,
      setAddingVariationFor,
      setConvertingProduct,
      setDeleting,
      expandedProducts,
      data,
    ],
  )

  // Counts for the apply-image dialog. Rows already showing the source's
  // image are in neither, matching what the server reports as updated.
  const applyTargets = applyingImageFor ? allDescendants(applyingImageFor) : []
  const applyMissingCount = applyTargets.filter((v) => !v.imageUrl).length
  const applyOverwriteCount = applyTargets.filter(
    (v) => v.imageUrl && v.imageUrl !== applyingImageFor?.imageUrl,
  ).length
  const applyCount =
    applyImageMode === 'missing' ? applyMissingCount : applyMissingCount + applyOverwriteCount

  return (
    <Page>
      <PageHeader
        title="Products"
        description="Phones tracked by IMEI, SIM cards tracked by ICCID, accessories counted by quantity, and service plans."
        actions={
          can('catalog:write') ? (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  const params = new URLSearchParams()
                  if (search) params.set('q', search)
                  if (typeFilter !== 'all') params.set('type', typeFilter)
                  if (categoryFilter !== 'all') params.set('category', categoryFilter)
                  if (deviceFilter.trim()) params.set('device', deviceFilter.trim())
                  const qs = params.toString()
                  window.location.assign(`${apiBaseUrl}/products/export.csv${qs ? `?${qs}` : ''}`)
                }}
              >
                <Download /> Export
              </Button>
              <Button variant="secondary" onClick={() => setImporting(true)}>
                <Upload /> Import
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setDraftEpoch((e) => e + 1)
                  setCreating(true)
                }}
              >
                <Plus /> New product
              </Button>
            </div>
          ) : null
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative max-w-xs flex-1">
          <Search
            className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-foreground-subtle"
            aria-hidden="true"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or SKU…"
            className="ps-8"
          />
        </div>
        <Select value={typeFilter} onValueChange={(value) => setTypeFilter(value)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="serialized">Phones</SelectItem>
            <SelectItem value="stock">Accessories</SelectItem>
            <SelectItem value="plan">Service plans</SelectItem>
            <SelectItem value="service">Services</SelectItem>
            <SelectItem value="sim">SIM cards</SelectItem>
            <SelectItem value="gift_card">Gift cards</SelectItem>
            <SelectItem value="digital_license">Digital licenses</SelectItem>
          </SelectContent>
        </Select>
        {categories && categories.length > 0 ? (
          <Select value={categoryFilter} onValueChange={(value) => setCategoryFilter(value)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.name}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        <Input
          value={deviceFilter}
          onChange={(e) => setDeviceFilter(e.target.value)}
          placeholder="Compatible device…"
          aria-label="Filter by compatible device"
          list="product-device-filter-options"
          autoComplete="off"
          className="w-44"
        />
        <datalist id="product-device-filter-options">
          {deviceSuggestions.map((d) => (
            <option key={d} value={d} />
          ))}
        </datalist>
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as typeof statusFilter)}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        data={rows}
        columns={columns}
        isLoading={isLoading}
        onRowClick={(row) => setQuickView(row)}
        {...(can('catalog:write')
          ? {
              getRowId: (row: TableRow) => row.id,
              selectedIds,
              onToggleRow: (id: string) =>
                setSelectedIds((prev) => {
                  const next = new Set(prev)
                  if (next.has(id)) next.delete(id)
                  else next.add(id)
                  return next
                }),
              onToggleAll: (ids: string[]) =>
                setSelectedIds((prev) => (prev.size === ids.length ? new Set() : new Set(ids))),
            }
          : {})}
        sort={{
          column: sort.column,
          order: sort.order,
          onChange: (column, order) => setSort({ column, order }),
        }}
        infinite={{
          total,
          hasMore: Boolean(hasNextPage),
          isFetchingMore: isFetchingNextPage,
          onLoadMore: () => void fetchNextPage(),
        }}
        empty={
          search ||
          typeFilter !== 'all' ||
          categoryFilter !== 'all' ||
          deviceFilter.trim() ||
          statusFilter !== 'all' ? (
            <EmptyState
              icon={Search}
              variant="no-results"
              title="No products match"
              description="Try a different search, or clear the filters."
              onClearFilters={() => {
                setSearch('')
                setTypeFilter('all')
                setCategoryFilter('all')
                setDeviceFilter('')
                setStatusFilter('all')
              }}
            />
          ) : (
            <EmptyState
              icon={Package}
              title="No products yet"
              description="Add the phones, accessories and plans you sell."
              action={
                can('catalog:write')
                  ? { label: 'Add a product', onClick: () => setCreating(true) }
                  : undefined
              }
            />
          )
        }
      />

      <BulkActionBar count={selectedIds.size} onClear={() => setSelectedIds(new Set())}>
        <Button variant="ghost" size="sm" onClick={() => setBulkEditing(true)}>
          Edit…
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setConfirmingBulkDeactivate(true)}>
          Deactivate
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setConfirmingBulkArchive(true)}>
          Archive
        </Button>
      </BulkActionBar>

      <CreateProductDialog
        key={duplicating?.id ?? `create-${draftEpoch}`}
        open={creating || duplicating !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false)
            setDuplicating(null)
          }
        }}
        initial={duplicating}
        onDiscardDraft={() => setDraftEpoch((e) => e + 1)}
      />
      <ProductImportDialog open={importing} onOpenChange={setImporting} />
      <EditProductDialog product={editing} onClose={() => setEditing(null)} />
      <ProductQuickViewDialog product={quickView} onClose={() => setQuickView(null)} />
      <AddVariationDialog
        key={
          addingVariationFor ? `${addingVariationFor.id}:${variationSource?.id ?? 'new'}` : 'closed'
        }
        parent={addingVariationFor}
        initial={variationSource}
        onClose={() => {
          setAddingVariationFor(null)
          setVariationSource(null)
        }}
      />
      <AttributePricesDialog
        key={pricingAttributesFor?.id}
        product={pricingAttributesFor}
        onClose={() => setPricingAttributesFor(null)}
      />
      <ChangeParentDialog
        key={convertingProduct?.id}
        product={convertingProduct}
        currentParent={
          convertingProduct?.parentId ? (findLoaded(convertingProduct.parentId) ?? null) : null
        }
        onClose={() => setConvertingProduct(null)}
      />

      <Dialog
        open={detachingProduct !== null}
        onOpenChange={(open) => !open && setDetachingProduct(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Detach {detachingProduct?.name} to a standalone product?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              It leaves {findLoaded(detachingProduct?.parentId)?.name ?? 'its parent'} and becomes
              its own product. It keeps its SKU, stock, order history and vending assignments.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDetachingProduct(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={detach.isPending}
              onClick={() => detachingProduct && detach.mutate(detachingProduct)}
            >
              Detach
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={applyingImageFor !== null}
        onOpenChange={(open) => !open && setApplyingImageFor(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Apply image to the variations of {applyingImageFor?.name}?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-3">
              <p className="text-sm text-foreground-muted">
                Copies this product&apos;s image onto the variations beneath it. Variations that
                already show this image are left as they are.
              </p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="apply-image-mode"
                  checked={applyImageMode === 'missing'}
                  disabled={applyMissingCount === 0}
                  onChange={() => setApplyImageMode('missing')}
                  className="size-4 accent-primary"
                />
                <span>Only variations without an image ({applyMissingCount})</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="apply-image-mode"
                  checked={applyImageMode === 'all'}
                  disabled={applyMissingCount + applyOverwriteCount === 0}
                  onChange={() => setApplyImageMode('all')}
                  className="size-4 accent-primary"
                />
                <span>All variations ({applyMissingCount + applyOverwriteCount})</span>
              </label>
              {applyMissingCount + applyOverwriteCount === 0 ? (
                <p className="text-sm text-foreground-muted">
                  Every variation already has this image.
                </p>
              ) : null}
              {applyImageMode === 'all' && applyOverwriteCount > 0 ? (
                <p className="text-sm text-danger">
                  This replaces the image on {applyOverwriteCount} variation
                  {applyOverwriteCount === 1 ? '' : 's'} that already{' '}
                  {applyOverwriteCount === 1 ? 'has' : 'have'} one.
                </p>
              ) : null}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setApplyingImageFor(null)}>
              Cancel
            </Button>
            <Button
              variant={applyImageMode === 'all' && applyOverwriteCount > 0 ? 'danger' : 'primary'}
              loading={applyImageToVariations.isPending}
              disabled={applyCount === 0}
              onClick={() =>
                applyingImageFor &&
                applyImageToVariations.mutate({ id: applyingImageFor.id, mode: applyImageMode })
              }
            >
              Apply image
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmingBulkDeactivate}
        onOpenChange={(open) => !open && setConfirmingBulkDeactivate(false)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Deactivate {selectedIds.size} product{selectedIds.size === 1 ? '' : 's'}?
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              They stop being offered for sale immediately. Any variations under a deactivated
              listing are deactivated with it. Order history is unaffected, and they can be
              reactivated later.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirmingBulkDeactivate(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={bulkUpdate.isPending}
              onClick={() => bulkUpdate.mutate({ isActive: false })}
            >
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmingBulkArchive}
        onOpenChange={(open) => !open && setConfirmingBulkArchive(false)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Archive {selectedIds.size} product{selectedIds.size === 1 ? '' : 's'}?
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              Archived products are permanently discontinued: they come off sale, and drop out of
              the Inactive list. Any variations under an archived listing are archived with it.
              Order and inventory history is unaffected, and they can be unarchived later.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirmingBulkArchive(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={bulkUpdate.isPending}
              onClick={() => bulkUpdate.mutate({ isArchived: true })}
            >
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {deleting?.name}?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              If this product has any order or inventory history it is archived instead of deleted,
              keeping that history intact. Its variations are deleted or archived along with it.
              This cannot be undone.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting.id)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkEditDialog
        open={bulkEditing}
        onOpenChange={setBulkEditing}
        count={selectedIds.size}
        pending={bulkUpdate.isPending}
        onSubmit={(set) => bulkUpdate.mutate(set)}
      />
    </Page>
  )
}

type FormValues = z.input<typeof createProductSchema>

// A single in-progress "New product" form, autosaved locally so a browser
// crash or accidental close doesn't lose what was typed. Duplicate never
// reads or writes this — it has its own prefill (the product being copied).
const PRODUCT_DRAFT_KEY = 'mv-product-draft'
const PRODUCT_DRAFT_VERSION = 1
const PRODUCT_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
const PRODUCT_DRAFT_DEBOUNCE_MS = 750

function blankProductDefaults(): Partial<FormValues> {
  return {
    type: 'serialized',
    isActive: true,
    isInternal: false,
    salesChannel: 'all',
    attributes: {},
    galleryImageUrls: [],
    compatibleDevices: [],
    priceCents: 0,
    salePriceCents: null,
    sortOrder: 100,
  }
}

function isBlankProductDraft(values: Partial<FormValues>): boolean {
  const blank = blankProductDefaults()
  return (Object.keys(values) as (keyof FormValues)[]).every((key) => {
    const v = values[key]
    if (v === undefined || v === null || v === '') return true
    if (key === 'attributes') return Object.keys(v as object).length === 0
    if (key === 'galleryImageUrls') return (v as string[]).length === 0
    if (key === 'compatibleDevices') return (v as string[]).length === 0
    return v === blank[key]
  })
}

// Deliberately not a zod parse: createProductSchema rejects a half-typed SKU
// or a blank required field, which is exactly what a draft looks like. This
// picks only known, correctly-typed keys — anything else (an old field name,
// a corrupted value) is dropped rather than failing the whole restore.
function sanitizeProductDraftValues(raw: unknown): Partial<FormValues> | null {
  if (typeof raw !== 'object' || raw === null) return null
  const obj = raw as Record<string, unknown>
  const out: Partial<FormValues> = {}
  const str = (k: keyof FormValues) => {
    const v = obj[k]
    if (typeof v === 'string') out[k] = v as never
    else if (v === null) out[k] = null as never
  }
  const num = (k: keyof FormValues) => {
    const v = obj[k]
    if (typeof v === 'number' && Number.isFinite(v)) out[k] = v as never
    else if (v === null) out[k] = null as never
  }
  const bool = (k: keyof FormValues) => {
    const v = obj[k]
    if (typeof v === 'boolean') out[k] = v as never
  }
  str('sku')
  str('name')
  str('description')
  str('category')
  str('brand')
  str('imageUrl')
  str('manufacturerSku')
  str('sourceUrl')
  num('priceCents')
  num('salePriceCents')
  num('costCents')
  bool('isActive')
  bool('isInternal')
  if (
    typeof obj.salesChannel === 'string' &&
    (PRODUCT_SALES_CHANNELS as readonly string[]).includes(obj.salesChannel)
  ) {
    out.salesChannel = obj.salesChannel as ProductSalesChannel
  }
  if (typeof obj.type === 'string' && (PRODUCT_TYPES as readonly string[]).includes(obj.type)) {
    out.type = obj.type as FormValues['type']
  }
  if (Array.isArray(obj.galleryImageUrls)) {
    out.galleryImageUrls = obj.galleryImageUrls.filter((u): u is string => typeof u === 'string')
  }
  if (Array.isArray(obj.compatibleDevices)) {
    out.compatibleDevices = obj.compatibleDevices.filter((d): d is string => typeof d === 'string')
  }
  if (typeof obj.attributes === 'object' && obj.attributes !== null) {
    const attrs: Record<string, string | number | boolean> = {}
    for (const [k, v] of Object.entries(obj.attributes as Record<string, unknown>)) {
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') attrs[k] = v
    }
    out.attributes = attrs
  }
  return out
}

function readProductDraft(): { values: Partial<FormValues>; savedAt: number } | null {
  try {
    const raw = window.localStorage.getItem(PRODUCT_DRAFT_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const obj = parsed as Record<string, unknown>
    if (obj.version !== PRODUCT_DRAFT_VERSION) return null
    if (typeof obj.savedAt !== 'number' || Date.now() - obj.savedAt > PRODUCT_DRAFT_MAX_AGE_MS) {
      return null
    }
    const values = sanitizeProductDraftValues(obj.values)
    if (!values || isBlankProductDraft(values)) return null
    return { values, savedAt: obj.savedAt }
  } catch {
    return null
  }
}

function writeProductDraft(values: Partial<FormValues>): void {
  try {
    if (isBlankProductDraft(values)) {
      window.localStorage.removeItem(PRODUCT_DRAFT_KEY)
      return
    }
    window.localStorage.setItem(
      PRODUCT_DRAFT_KEY,
      JSON.stringify({ version: PRODUCT_DRAFT_VERSION, savedAt: Date.now(), values }),
    )
  } catch {
    // Storage full or disabled: the draft just won't survive a crash.
  }
}

function clearProductDraft(): void {
  try {
    window.localStorage.removeItem(PRODUCT_DRAFT_KEY)
  } catch {
    // Same as above — nothing to recover from here.
  }
}

/**
 * Radix reads an empty string as "no value chosen", which would make the
 * placeholder and a deliberate "no category" indistinguishable — so the
 * absence gets a name of its own and is mapped back at the edges.
 */
const SALES_CHANNEL_LABEL: Record<ProductSalesChannel, string> = {
  all: 'Website and kiosk',
  web: 'Website only',
  kiosk: 'Kiosk only (kiosk, vending machines)',
}
const SALES_CHANNEL_BADGE: Record<ProductSalesChannel, string> = {
  all: 'Website and kiosk',
  web: 'Website only',
  kiosk: 'Kiosk only',
}

function SalesChannelSelect({
  value,
  onChange,
  disabled,
  props,
}: {
  value: ProductSalesChannel
  onChange: (value: ProductSalesChannel) => void
  disabled?: boolean
  props: Record<string, unknown>
}) {
  return (
    <Select
      value={value}
      onValueChange={(v) => onChange(v as ProductSalesChannel)}
      disabled={disabled}
    >
      <SelectTrigger {...props}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {PRODUCT_SALES_CHANNELS.map((c) => (
          <SelectItem key={c} value={c}>
            {SALES_CHANNEL_LABEL[c]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

const UNCATEGORISED = '__none__'

/** Matches the API's own cap (apps/api/src/modules/catalog/routes.ts). */
const MAX_IMAGE_BYTES = 5 * 1024 * 1024

/**
 * A small look at the image a staff member just pasted a link to, so a
 * typo'd URL shows up before Save rather than after. Keyed on `url` at the
 * call site so a changed link remounts it, resetting `broken` without an
 * effect.
 */
function ImagePreview({ url }: { url?: string | null }) {
  const [broken, setBroken] = useState(false)

  if (!url || broken) return null

  return (
    <img
      src={url}
      alt=""
      className="h-20 w-20 rounded-md border border-border bg-surface-sunken object-cover"
      onError={() => setBroken(true)}
    />
  )
}

/**
 * The product image field: paste a link, or hand it a file directly — by
 * dropping one on the box below or clicking it to browse. Either way the
 * file is uploaded right away and `onChange` gets back the URL it now lives
 * at, the same shape as if someone had pasted it.
 */
function ImagePicker({
  value,
  onChange,
  error,
  aiHints,
}: {
  value?: string | null
  onChange: (url: string | null) => void
  error?: string
  /** Product name/brand/category, folded into the default AI image prompt. */
  aiHints?: Record<string, string>
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)

  async function upload(file: File) {
    if (!file.type.startsWith('image/') && !/\.jxl$/i.test(file.name)) {
      toast.error('Could not upload', 'Only image files are supported.')
      return
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error('Could not upload', 'Images must be 5 MB or smaller.')
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const { url } = await api.postFile<{ url: string }>('/products/images', formData)
      onChange(url)
      void queryClient.invalidateQueries({ queryKey: queryKeys.imageLibrary.all() })
    } catch (err) {
      toast.error('Could not upload the image', err instanceof ApiError ? err.message : undefined)
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-2">
      <Field
        label="Image URL"
        error={error}
        hint="Paste a link, or drop a picture below to upload one."
      >
        {(props) => (
          <Input
            {...props}
            type="url"
            value={value ?? ''}
            invalid={!!error}
            onChange={(e) => {
              const v = e.target.value
              onChange(v.trim() ? v.trim() : null)
            }}
          />
        )}
      </Field>

      <button
        type="button"
        disabled={uploading}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          const file = e.dataTransfer.files[0]
          if (file) upload(file)
        }}
        className={`flex w-full items-center justify-center rounded-md border-2 border-dashed px-4 py-3 text-sm transition-colors disabled:opacity-50 ${
          dragOver
            ? 'border-primary bg-primary-subtle text-foreground'
            : 'border-border text-foreground-muted hover:border-border-strong'
        }`}
      >
        {uploading ? 'Uploading…' : 'Drop an image here, or click to upload'}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.jxl"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) upload(file)
          e.target.value = ''
        }}
      />

      <div className="flex items-center gap-3">
        <ImagePreview key={value} url={value} />
        <AiImageButton
          sourceImageId={imageIdFromUrl(value)}
          promptHints={aiHints}
          onApply={onChange}
        />
        <Button type="button" variant="secondary" size="sm" onClick={() => setLibraryOpen(true)}>
          <Images /> Choose from library
        </Button>
      </div>
      <ImageLibraryDialog open={libraryOpen} onOpenChange={setLibraryOpen} onSelect={onChange} />
    </div>
  )
}

/** Uploads one picture and returns the URL it now lives at. Throws a plain
 *  Error with a message fit to show as-is when the file is turned away. */
async function uploadProductImage(file: File): Promise<string> {
  if (!file.type.startsWith('image/') && !/\.jxl$/i.test(file.name)) {
    throw new Error('Only image files are supported.')
  }
  if (file.size > MAX_IMAGE_BYTES) throw new Error('Images must be 5 MB or smaller.')
  const formData = new FormData()
  formData.append('file', file)
  const { url } = await api.postFile<{ url: string }>('/products/images', formData)
  return url
}

type ProductImages = { imageUrl: string | null; galleryImageUrls: string[] }

/**
 * A product's pictures: the main one (the existing ImagePicker, used on
 * the shop card and everywhere a single image is shown) plus up to
 * MAX_GALLERY_IMAGES more that appear in the gallery on the product page.
 * Extras can be reordered, removed, or promoted to main — promoting swaps
 * it with the current main, so nothing is lost. Nothing is saved here: the
 * parent form sends both fields with the rest of the product.
 */
function ProductImagesEditor({
  imageUrl,
  galleryImageUrls,
  onChange,
  error,
  aiHints,
}: {
  imageUrl: string | null
  galleryImageUrls: string[]
  onChange: (next: ProductImages) => void
  error?: string
  aiHints?: Record<string, string>
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)

  const room = MAX_GALLERY_IMAGES - galleryImageUrls.length
  const setGallery = (next: string[]) => onChange({ imageUrl, galleryImageUrls: next })

  async function addFiles(files: File[]) {
    if (files.length === 0) return
    const accepted = files.slice(0, Math.max(room, 0))
    if (accepted.length < files.length) {
      toast.error(
        'Some pictures were skipped',
        `A product can have up to ${MAX_GALLERY_IMAGES} extra pictures.`,
      )
    }

    setUploading(true)
    // One request per file (the endpoint takes a single file), added to the
    // list as each lands so a failure part-way keeps what already uploaded.
    let current = galleryImageUrls
    try {
      for (const file of accepted) {
        try {
          const url = await uploadProductImage(file)
          if (url === imageUrl || current.includes(url)) continue
          current = [...current, url]
          onChange({ imageUrl, galleryImageUrls: current })
          void queryClient.invalidateQueries({ queryKey: queryKeys.imageLibrary.all() })
        } catch (err) {
          toast.error(
            `Could not upload ${file.name}`,
            err instanceof ApiError || err instanceof Error ? err.message : undefined,
          )
        }
      }
    } finally {
      setUploading(false)
    }
  }

  function move(index: number, by: -1 | 1) {
    const next = [...galleryImageUrls]
    const target = index + by
    const [moved] = next.splice(index, 1)
    if (moved === undefined || target < 0 || target > next.length) return
    next.splice(target, 0, moved)
    setGallery(next)
  }

  function makeMain(index: number) {
    const picked = galleryImageUrls[index]
    if (!picked) return
    const next = [...galleryImageUrls]
    if (imageUrl) next[index] = imageUrl
    else next.splice(index, 1)
    onChange({ imageUrl: picked, galleryImageUrls: next })
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-medium">Main image</p>
        <ImagePicker
          value={imageUrl}
          onChange={(url) => onChange({ imageUrl: url, galleryImageUrls })}
          error={error}
          aiHints={aiHints}
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">
          More pictures{' '}
          <span className="font-normal text-foreground-muted">
            ({galleryImageUrls.length} of {MAX_GALLERY_IMAGES}) — shown in the gallery on the
            product page
          </span>
        </p>

        {galleryImageUrls.length > 0 ? (
          <ul className="flex flex-wrap gap-3">
            {galleryImageUrls.map((url, index) => (
              <li key={url} className="w-24 space-y-1">
                <img
                  src={url}
                  alt=""
                  className="h-24 w-24 rounded-md border border-border bg-surface-sunken object-cover"
                />
                <div className="flex justify-between">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-7 px-1.5"
                    aria-label="Move earlier"
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowLeft />
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-7 px-1.5"
                    aria-label="Make this the main image"
                    title="Make this the main image"
                    onClick={() => makeMain(index)}
                  >
                    <Star />
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-7 px-1.5"
                    aria-label="Remove this picture"
                    onClick={() => setGallery(galleryImageUrls.filter((_, i) => i !== index))}
                  >
                    <X />
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-7 px-1.5"
                    aria-label="Move later"
                    disabled={index === galleryImageUrls.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowRight />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : null}

        {room > 0 ? (
          <>
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault()
                setDragOver(true)
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault()
                setDragOver(false)
                void addFiles(Array.from(e.dataTransfer.files))
              }}
              className={`flex w-full items-center justify-center rounded-md border-2 border-dashed px-4 py-3 text-sm transition-colors disabled:opacity-50 ${
                dragOver
                  ? 'border-primary bg-primary-subtle text-foreground'
                  : 'border-border text-foreground-muted hover:border-border-strong'
              }`}
            >
              {uploading ? 'Uploading…' : 'Drop pictures here, or click to add several at once'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.jxl"
              className="hidden"
              onChange={(e) => {
                void addFiles(Array.from(e.target.files ?? []))
                e.target.value = ''
              }}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setLibraryOpen(true)}
            >
              <Images /> Add from library
            </Button>
            <ImageLibraryDialog
              open={libraryOpen}
              onOpenChange={setLibraryOpen}
              onSelect={(url) => {
                if (url === imageUrl || galleryImageUrls.includes(url)) {
                  toast.error('Already on this product', 'That picture is already in use here.')
                  return
                }
                setGallery([...galleryImageUrls, url])
              }}
            />
          </>
        ) : (
          <p className="text-sm text-foreground-muted">
            That is the most a product can have — remove one to add another.
          </p>
        )}
      </div>
    </div>
  )
}

type AttributeRow = { id: number; key: string; value: string }

/** Coerces an edited attribute value back to the type the API expects. */
function coerceAttributeValue(raw: string): string | number | boolean {
  if (raw === 'true') return true
  if (raw === 'false') return false
  if (raw.trim() !== '' && !Number.isNaN(Number(raw))) return Number(raw)
  return raw
}

function attributeRowsToRecord(rows: AttributeRow[]): Record<string, string | number | boolean> {
  const record: Record<string, string | number | boolean> = {}
  for (const row of rows) {
    const key = row.key.trim()
    if (!key) continue
    record[key] = coerceAttributeValue(row.value)
  }
  return record
}

/**
 * Free-form product attributes (color, storage size, …) as name/value rows.
 *
 * Rows are kept as an array rather than editing the record directly: a record
 * can't hold an empty or momentarily-duplicate key while it's mid-typed, and
 * that would make a row vanish or merge with another one as you type it.
 *
 * The Name and Value inputs offer the shared attribute definitions (t0919) as
 * <datalist> suggestions — the same free-text-with-suggestions convention as
 * the vendor field in intake.tsx. A datalist never constrains what's typed, so
 * a one-off value (Gold) types and saves exactly as before; nothing here ever
 * writes back to the shared definitions.
 */
function AttributesEditor({
  value,
  onChange,
}: {
  value: Record<string, string | number | boolean>
  onChange: (next: Record<string, string | number | boolean>) => void
}) {
  const nextId = useRef(0)
  const [rows, setRows] = useState<AttributeRow[]>(() =>
    Object.entries(value).map(([key, v]) => ({ id: nextId.current++, key, value: String(v) })),
  )

  const listId = useId()
  const { data: definitions } = useQuery({
    queryKey: queryKeys.products.attributeDefinitions(),
    queryFn: () => api.get<ProductAttributeDefinition[]>('/product-attributes'),
    staleTime: 60_000,
  })
  const findDefinition = (key: string) => {
    const normalized = key.trim().toLowerCase()
    if (!normalized) return undefined
    return definitions?.find((d) => d.name.toLowerCase() === normalized)
  }

  function commit(next: AttributeRow[]) {
    setRows(next)
    onChange(attributeRowsToRecord(next))
  }

  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <div key={row.id} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2">
          <Input
            placeholder="Name"
            value={row.key}
            list={`${listId}-names`}
            autoComplete="off"
            onChange={(e) =>
              commit(rows.map((r) => (r.id === row.id ? { ...r, key: e.target.value } : r)))
            }
          />
          <Input
            placeholder="Value"
            value={row.value}
            list={`${listId}-choices-${row.id}`}
            autoComplete="off"
            onChange={(e) =>
              commit(rows.map((r) => (r.id === row.id ? { ...r, value: e.target.value } : r)))
            }
          />
          <datalist id={`${listId}-choices-${row.id}`}>
            {(findDefinition(row.key)?.choices ?? []).map((choice) => (
              <option key={choice} value={choice} />
            ))}
          </datalist>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Remove attribute"
            onClick={() => commit(rows.filter((r) => r.id !== row.id))}
          >
            <X />
          </Button>
        </div>
      ))}
      <datalist id={`${listId}-names`}>
        {(definitions ?? []).map((d) => (
          <option key={d.id} value={d.name} />
        ))}
      </datalist>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => commit([...rows, { id: nextId.current++, key: '', value: '' }])}
      >
        <Plus /> Add attribute
      </Button>
    </div>
  )
}

/**
 * Fills the SKU field from the formula (title initials + creation-order
 * numbers + today's date) without submitting the form — a manual trigger,
 * not an auto-fill, per how the client asked for this. The result lands in
 * the same registered SKU field, so it stays editable afterward like any
 * manually-typed SKU.
 */
function GenerateSkuButton({
  name,
  parentId,
  onGenerated,
}: {
  name: string
  parentId?: string
  onGenerated: (sku: string) => void
}) {
  const toast = useToast()
  const [generating, setGenerating] = useState(false)

  async function handleClick() {
    setGenerating(true)
    try {
      const { sku } = await api.get<GenerateSkuResponse>('/products/generate-sku', {
        query: { name, ...(parentId ? { parentId } : {}) },
      })
      onGenerated(sku)
    } catch (error) {
      toast.error('Could not generate a SKU', error instanceof ApiError ? error.message : undefined)
    } finally {
      setGenerating(false)
    }
  }

  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      disabled={!name.trim() || generating}
      loading={generating}
      onClick={handleClick}
    >
      Generate
    </Button>
  )
}

/** The categories a product may be filed under: the live ones, in shop order. */
function useCategoryOptions(): ProductCategory[] {
  const { data } = useQuery({
    queryKey: queryKeys.products.categories(),
    queryFn: () => api.get<ProductCategory[]>('/categories'),
  })
  return (data ?? []).filter((c) => c.isActive)
}

/** The rates a product or service can be taxed at — set up under
 *  Settings > Shop > Sales tax. */
function useTaxCategoryOptions(): TaxCategory[] {
  const { data } = useQuery({
    queryKey: queryKeys.taxCategories(),
    queryFn: () => api.get<TaxCategory[]>('/tax-categories'),
  })
  return data ?? []
}

/** Mirrors UNCATEGORISED below: Radix dislikes an empty item value. */
const NON_TAXABLE = '__non_taxable__'

function TaxCategoryField({
  value,
  onChange,
}: {
  value: string | null | undefined
  onChange: (taxCategoryId: string | null) => void
}) {
  const taxCategoryOptions = useTaxCategoryOptions()
  return (
    <Field
      label="Sales tax"
      hint={
        taxCategoryOptions.length === 0
          ? 'No tax categories yet — add one under Settings > Shop > Sales tax.'
          : undefined
      }
    >
      {(props) => (
        <Select
          value={value ?? NON_TAXABLE}
          onValueChange={(v) => onChange(v === NON_TAXABLE ? null : v)}
        >
          <SelectTrigger {...props}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NON_TAXABLE}>Non-taxable</SelectItem>
            {taxCategoryOptions.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name} — {(c.rateBps / 100).toFixed(2)}%
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>
  )
}

function CreateProductDialog({
  open,
  onOpenChange,
  initial,
  onDiscardDraft,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Duplicating an existing product: prefill its fields into this
   *  otherwise-blank create form. SKU is deliberately left out — it's
   *  unique per product, so staff generate or type a fresh one. */
  initial?: ProductRow | null
  /** Called when the admin discards a restored draft — the parent remounts
   *  this dialog with a blank form. Only relevant on the plain create path. */
  onDiscardDraft?: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const categoryOptions = useCategoryOptions()

  // Restored once, at mount — the parent remounts this dialog (a fresh `key`)
  // each time it's opened for a plain create, so "at mount" means "now".
  // Duplicating has its own prefill and must not read or clobber a pending
  // create draft.
  const [draft] = useState(() => (initial ? null : readProductDraft()))
  const [defaults] = useState<Partial<FormValues>>(() =>
    initial
      ? {
          type: initial.type,
          isActive: true,
          isInternal: initial.isInternal,
          salesChannel: initial.salesChannel,
          attributes: { ...initial.attributes },
          priceCents: initial.priceCents,
          salePriceCents: initial.salePriceCents,
          costCents: initial.costCents,
          category: initial.category,
          taxCategoryId: initial.taxCategoryId,
          brand: initial.brand,
          imageUrl: initial.imageUrl,
          galleryImageUrls: [...initial.galleryImageUrls],
          compatibleDevices: [...initial.compatibleDevices],
          manufacturerSku: initial.manufacturerSku,
          sourceUrl: initial.sourceUrl,
          description: initial.description,
          name: `${initial.name} (copy)`,
          sortOrder: initial.sortOrder,
          giftCardKind: initial.giftCardKind,
        }
      : { ...blankProductDefaults(), ...draft?.values },
  )
  const draftClearedRef = useRef(false)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues, unknown, CreateProductInput>({
    // The exact schema the API validates against: the form cannot accept
    // something the server will then reject. Input and output types differ
    // because the schema fills in defaults, so both are named here.
    resolver: zodResolver(createProductSchema),
    defaultValues: defaults,
  })

  const type = watch('type')
  const category = watch('category')
  // Filed under a pinned category, a product's type is not really a choice —
  // an accessories shelf holding a phone tracked by IMEI can never have a
  // provisioned handset behind it, so it would sit at zero available forever.
  const pinnedType = categoryOptions.find((c) => c.name === category)?.allowedType ?? null

  useEffect(() => {
    if (pinnedType && type !== pinnedType) setValue('type', pinnedType)
  }, [pinnedType, type, setValue])

  // A gift card is either emailed or handed over; anything else is neither.
  const giftCardKind = watch('giftCardKind')
  useEffect(() => {
    if (type === 'gift_card' && !giftCardKind) setValue('giftCardKind', 'digital')
    if (type !== 'gift_card' && giftCardKind) setValue('giftCardKind', null)
  }, [type, giftCardKind, setValue])

  // Debounced local autosave of the in-progress create form, so closing the
  // browser or a crash before Save doesn't lose what was typed. Skipped for
  // Duplicate — that form is disposable prefill, not something to recover.
  useEffect(() => {
    if (initial) return
    let timer: ReturnType<typeof setTimeout> | undefined
    let pending: Partial<FormValues> | null = null
    const flush = () => {
      if (pending && !draftClearedRef.current) writeProductDraft(pending)
      pending = null
    }
    const subscription = watch((values) => {
      pending = values as Partial<FormValues>
      clearTimeout(timer)
      timer = setTimeout(flush, PRODUCT_DRAFT_DEBOUNCE_MS)
    })
    return () => {
      subscription.unsubscribe()
      clearTimeout(timer)
      // Closing the dialog (Cancel, Escape, backdrop click) shouldn't lose
      // the last stretch of typing that hadn't hit the debounce yet — that's
      // the whole point of keeping the draft on close.
      flush()
    }
  }, [watch, initial])

  function discardDraft() {
    draftClearedRef.current = true
    clearProductDraft()
    onDiscardDraft?.()
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      await api.post('/products', values)
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      toast.success('Product created')
      if (!initial) {
        draftClearedRef.current = true
        clearProductDraft()
      }
      reset()
      onOpenChange(false)
    } catch (error) {
      toast.error(
        'Could not create the product',
        error instanceof ApiError ? error.message : undefined,
      )
    }
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial ? `Duplicate ${initial.name}` : 'New product'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <DialogBody className="space-y-4">
            {draft && (
              <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2 text-sm text-foreground-muted">
                <span>Restored an unsaved draft.</span>
                <Button type="button" variant="ghost" size="sm" onClick={discardDraft}>
                  Start over
                </Button>
              </div>
            )}
            <Field
              label="Category"
              error={errors.category?.message}
              hint={
                categoryOptions.length === 0
                  ? 'No categories yet — add one under Categories first.'
                  : 'A category that only holds one type sets the type below for you.'
              }
            >
              {(props) => (
                <Select
                  value={category ?? UNCATEGORISED}
                  onValueChange={(value) =>
                    setValue('category', value === UNCATEGORISED ? null : value)
                  }
                >
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCATEGORISED}>No category</SelectItem>
                    {categoryOptions.map((c) => (
                      <SelectItem key={c.id} value={c.name}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field
              label="Type"
              required
              hint={
                pinnedType
                  ? `Set by the category — ${TYPE_LABEL[pinnedType]}.`
                  : 'Can be changed later, but only while there is no stock on hand.'
              }
            >
              {(props) => (
                <Select
                  value={type}
                  disabled={!!pinnedType}
                  onValueChange={(value) => setValue('type', value as ProductType)}
                >
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="serialized">Phone (tracked by IMEI)</SelectItem>
                    <SelectItem value="stock">
                      Counted stock (accessories, or phones not tracked by IMEI)
                    </SelectItem>
                    <SelectItem value="plan">Service plan (recurring)</SelectItem>
                    <SelectItem value="service">Service (labour, one-off)</SelectItem>
                    <SelectItem value="sim">SIM card (tracked by ICCID)</SelectItem>
                    <SelectItem value="gift_card">Gift card (stored value)</SelectItem>
                    <SelectItem value="digital_license">
                      Digital license (tracked by license number)
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>

            {type === 'gift_card' && (
              <Field
                label="Delivery"
                required
                hint="An emailed card is sent to the buyer's recipient the moment it is paid for. A physical card is handed over at the counter, and you attach its code from the Gift cards screen."
              >
                {(props) => (
                  <Select
                    value={giftCardKind ?? 'digital'}
                    onValueChange={(value) =>
                      setValue('giftCardKind', value as 'digital' | 'physical', {
                        shouldDirty: true,
                      })
                    }
                  >
                    <SelectTrigger {...props}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="digital">Digital — emailed or shown on screen</SelectItem>
                      <SelectItem value="physical">Physical — a card handed over</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </Field>
            )}

            <PlanBillingSection productId={null} type={type} />

            <Field
              label="Shop use only"
              hint="Tracked in stock but never offered for sale on the storefront, kiosk, or a vending machine. Toggle it off later to sell it."
            >
              {(props) => (
                <Switch
                  {...props}
                  checked={watch('isInternal') ?? false}
                  onCheckedChange={(checked) =>
                    setValue('isInternal', checked, { shouldDirty: true })
                  }
                />
              )}
            </Field>

            <Field
              label="Where it's sold"
              hint="Choose whether customers can buy this on the website, on the kiosk and vending machines, or both."
            >
              {(props) => (
                <SalesChannelSelect
                  props={props}
                  value={watch('salesChannel') ?? 'all'}
                  onChange={(v) => setValue('salesChannel', v, { shouldDirty: true })}
                />
              )}
            </Field>

            <Field label="Name" error={errors.name?.message} required>
              {(props) => <Input {...props} {...register('name')} invalid={!!errors.name} />}
            </Field>

            <Field label="SKU" error={errors.sku?.message} required>
              {(props) => (
                <div className="flex gap-2">
                  <Input {...props} {...register('sku')} identifier invalid={!!errors.sku} />
                  <GenerateSkuButton
                    name={watch('name') ?? ''}
                    onGenerated={(sku) =>
                      setValue('sku', sku, { shouldValidate: true, shouldDirty: true })
                    }
                  />
                </div>
              )}
            </Field>

            <Field label="Price" error={errors.priceCents?.message} required hint="In dollars.">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  step="0.01"
                  min="0"
                  defaultValue={
                    defaults.priceCents ? (defaults.priceCents / 100).toFixed(2) : undefined
                  }
                  invalid={!!errors.priceCents}
                  onChange={(e) =>
                    setValue('priceCents', Math.round(Number(e.target.value || 0) * 100))
                  }
                />
              )}
            </Field>

            {type !== 'gift_card' && (
              <Field
                label="Sale price"
                error={errors.salePriceCents?.message}
                hint="Optional. Shown struck through next to the regular price. Leave blank for no sale."
              >
                {(props) => (
                  <Input
                    {...props}
                    type="number"
                    step="0.01"
                    min="0"
                    defaultValue={
                      defaults.salePriceCents != null
                        ? (defaults.salePriceCents / 100).toFixed(2)
                        : undefined
                    }
                    invalid={!!errors.salePriceCents}
                    onChange={(e) => {
                      const raw = e.target.value
                      setValue(
                        'salePriceCents',
                        raw.trim() === '' ? null : Math.round(Number(raw) * 100),
                        { shouldValidate: true },
                      )
                    }}
                  />
                )}
              </Field>
            )}

            <TaxCategoryField
              value={watch('taxCategoryId')}
              onChange={(v) => setValue('taxCategoryId', v, { shouldDirty: true })}
            />

            <Field
              label="Cost"
              error={errors.costCents?.message}
              hint="Optional. In dollars — what you pay, not the sale price. Feeds the cost of goods sold report."
            >
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  step="0.01"
                  defaultValue={
                    defaults.costCents != null ? (defaults.costCents / 100).toFixed(2) : undefined
                  }
                  min="0"
                  invalid={!!errors.costCents}
                  onChange={(e) => {
                    const raw = e.target.value
                    setValue(
                      'costCents',
                      raw.trim() === '' ? null : Math.round(Number(raw) * 100),
                      {
                        shouldValidate: true,
                      },
                    )
                  }}
                />
              )}
            </Field>

            <Field
              label="Order"
              error={errors.sortOrder?.message}
              hint="Low numbers come first on the shop page."
            >
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  max={9999}
                  defaultValue={defaults.sortOrder}
                  {...register('sortOrder', { valueAsNumber: true })}
                  invalid={!!errors.sortOrder}
                />
              )}
            </Field>

            <Field label="Brand" error={errors.brand?.message}>
              {(props) => (
                <Input
                  {...props}
                  {...register('brand', {
                    setValueAs: (v: string | null) => (v?.trim() ? v.trim() : null),
                  })}
                />
              )}
            </Field>

            <Field
              label="Manufacturer SKU"
              error={errors.manufacturerSku?.message}
              hint="The manufacturer's own part number, if different from the SKU above. Admin-only — never shown to customers."
            >
              {(props) => (
                <Input
                  {...props}
                  {...register('manufacturerSku', {
                    setValueAs: (v: string | null) => (v?.trim() ? v.trim() : null),
                  })}
                />
              )}
            </Field>

            <Field
              label="Source link"
              error={errors.sourceUrl?.message}
              hint="Where to view or order this product from the supplier or manufacturer. Admin-only — never shown to customers."
            >
              {(props) => (
                <Input
                  {...props}
                  type="url"
                  {...register('sourceUrl', {
                    setValueAs: (v: string | null) => (v?.trim() ? v.trim() : null),
                  })}
                  invalid={!!errors.sourceUrl}
                />
              )}
            </Field>

            <ProductImagesEditor
              imageUrl={watch('imageUrl') ?? null}
              galleryImageUrls={watch('galleryImageUrls') ?? []}
              onChange={(next) => {
                setValue('imageUrl', next.imageUrl, { shouldValidate: true, shouldDirty: true })
                setValue('galleryImageUrls', next.galleryImageUrls, { shouldDirty: true })
              }}
              error={errors.imageUrl?.message}
              aiHints={{ product: watch('name'), brand: watch('brand') ?? '' }}
            />

            <Field label="Description" error={errors.description?.message}>
              {(props) => (
                <RichTextEditor
                  {...props}
                  invalid={!!errors.description}
                  value={watch('description') ?? null}
                  onChange={(html) =>
                    setValue('description', html, { shouldValidate: true, shouldDirty: true })
                  }
                  aiAssist={{
                    fieldLabel: 'product description',
                    hints: {
                      Product: watch('name'),
                      Brand: watch('brand') ?? '',
                      Category: watch('category') ?? '',
                    },
                    imageId: imageIdFromUrl(watch('imageUrl')),
                    maxLength: 4000,
                  }}
                />
              )}
            </Field>

            <Field label="Attributes" hint="Free-form details like color or storage size.">
              {() => (
                <AttributesEditor
                  value={watch('attributes') ?? {}}
                  onChange={(next) =>
                    setValue('attributes', next, { shouldValidate: true, shouldDirty: true })
                  }
                />
              )}
            </Field>

            <Field
              label="Compatible devices"
              hint="Phones or models this fits (for cases, chargers, screen protectors…). Lets customers and staff search by device."
            >
              {() => (
                <CompatibleDevicesEditor
                  value={watch('compatibleDevices') ?? []}
                  onChange={(next) => setValue('compatibleDevices', next, { shouldDirty: true })}
                />
              )}
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isSubmitting}>
              Create product
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Adding a variation under an existing listing.
 *
 * A thin version of `CreateProductDialog`: type, category and brand come
 * from the parent and aren't asked for again — a variation's whole point is
 * that it shares those with its siblings, differing only in the things this
 * form does ask for (SKU, price, attributes, optionally its own image).
 */
function AddVariationDialog({
  parent,
  onClose,
  initial,
}: {
  parent: ProductRow | null
  onClose: () => void
  /** Duplicating an existing variation of this same parent — prefills the
   *  fields this form has. Description, cost and shop-use-only aren't asked
   *  here (same as for a hand-created variation), so they aren't copied. */
  initial?: ProductRow | null
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues, unknown, CreateProductInput>({
    resolver: zodResolver(createProductSchema),
    defaultValues: {
      type: parent?.type ?? 'stock',
      giftCardKind: parent?.giftCardKind ?? null,
      isActive: true,
      attributes: initial ? { ...initial.attributes } : {},
      priceCents: initial?.priceCents ?? parent?.priceCents ?? 0,
      salePriceCents: initial?.salePriceCents ?? null,
      category: parent?.category ?? null,
      taxCategoryId: initial?.taxCategoryId ?? parent?.taxCategoryId ?? null,
      brand: parent?.brand ?? null,
      imageUrl: initial?.imageUrl ?? parent?.imageUrl ?? null,
      galleryImageUrls: initial ? [...initial.galleryImageUrls] : [],
      manufacturerSku: initial?.manufacturerSku ?? null,
      sourceUrl: initial?.sourceUrl ?? null,
      parentId: parent?.id ?? null,
      name: initial ? `${initial.name} (copy)` : parent ? `${parent.name} — ` : '',
      sortOrder: initial?.sortOrder ?? 100,
    },
  })

  const onSubmit = handleSubmit(async (values) => {
    try {
      await api.post('/products', values)
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      toast.success('Variation created')
      reset()
      onClose()
    } catch (error) {
      toast.error(
        'Could not create the variation',
        error instanceof ApiError ? error.message : undefined,
      )
    }
  })

  return (
    <Dialog open={parent !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {initial ? `Duplicate ${initial.name}` : `New variation of ${parent?.name}`}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <DialogBody className="space-y-4">
            <Field label="Name" error={errors.name?.message} required>
              {(props) => <Input {...props} {...register('name')} invalid={!!errors.name} />}
            </Field>

            <Field label="SKU" error={errors.sku?.message} required>
              {(props) => (
                <div className="flex gap-2">
                  <Input {...props} {...register('sku')} identifier invalid={!!errors.sku} />
                  <GenerateSkuButton
                    name={watch('name') ?? ''}
                    parentId={parent?.id}
                    onGenerated={(sku) =>
                      setValue('sku', sku, { shouldValidate: true, shouldDirty: true })
                    }
                  />
                </div>
              )}
            </Field>

            <Field label="Price" error={errors.priceCents?.message} required hint="In dollars.">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  step="0.01"
                  min="0"
                  defaultValue={
                    initial
                      ? (initial.priceCents / 100).toFixed(2)
                      : parent
                        ? (parent.priceCents / 100).toFixed(2)
                        : undefined
                  }
                  invalid={!!errors.priceCents}
                  onChange={(e) =>
                    setValue('priceCents', Math.round(Number(e.target.value || 0) * 100))
                  }
                />
              )}
            </Field>

            {parent?.type !== 'gift_card' && (
              <Field
                label="Sale price"
                error={errors.salePriceCents?.message}
                hint="Optional. Shown struck through next to the regular price. Leave blank for no sale."
              >
                {(props) => (
                  <Input
                    {...props}
                    type="number"
                    step="0.01"
                    min="0"
                    defaultValue={
                      initial?.salePriceCents != null
                        ? (initial.salePriceCents / 100).toFixed(2)
                        : undefined
                    }
                    invalid={!!errors.salePriceCents}
                    onChange={(e) => {
                      const raw = e.target.value
                      setValue(
                        'salePriceCents',
                        raw.trim() === '' ? null : Math.round(Number(raw) * 100),
                        { shouldValidate: true },
                      )
                    }}
                  />
                )}
              </Field>
            )}

            <ProductImagesEditor
              imageUrl={watch('imageUrl') ?? null}
              galleryImageUrls={watch('galleryImageUrls') ?? []}
              onChange={(next) => {
                setValue('imageUrl', next.imageUrl, { shouldValidate: true, shouldDirty: true })
                setValue('galleryImageUrls', next.galleryImageUrls, { shouldDirty: true })
              }}
              error={errors.imageUrl?.message}
              aiHints={{ product: watch('name'), brand: parent?.brand ?? '' }}
            />

            <Field label="Attributes" hint="What sets this variation apart — color, size, storage…">
              {() => (
                <AttributesEditor
                  value={watch('attributes') ?? {}}
                  onChange={(next) =>
                    setValue('attributes', next, { shouldValidate: true, shouldDirty: true })
                  }
                />
              )}
            </Field>

            <Field
              label="Order"
              error={errors.sortOrder?.message}
              hint="Low numbers come first in the variation picker."
            >
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  max={9999}
                  defaultValue={initial?.sortOrder ?? 100}
                  {...register('sortOrder', { valueAsNumber: true })}
                  invalid={!!errors.sortOrder}
                />
              )}
            </Field>

            <Field
              label="Manufacturer SKU"
              error={errors.manufacturerSku?.message}
              hint="This variation's own manufacturer part number, if different from its SKU. Admin-only — never shown to customers."
            >
              {(props) => (
                <Input
                  {...props}
                  {...register('manufacturerSku', {
                    setValueAs: (v: string | null) => (v?.trim() ? v.trim() : null),
                  })}
                />
              )}
            </Field>

            <Field
              label="Source link"
              error={errors.sourceUrl?.message}
              hint="Where to view or order this variation from the supplier or manufacturer. Admin-only — never shown to customers."
            >
              {(props) => (
                <Input
                  {...props}
                  type="url"
                  {...register('sourceUrl', {
                    setValueAs: (v: string | null) => (v?.trim() ? v.trim() : null),
                  })}
                  invalid={!!errors.sourceUrl}
                />
              )}
            </Field>

            <p className="font-identifier text-2xs text-foreground-muted">
              {parent ? TYPE_LABEL[parent.type] : ''} · same category and brand as {parent?.name}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isSubmitting}>
              Create variation
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Converting an existing standalone product into a variation of another one
 * in place — for the case where staff created what should have been
 * variations (e.g. "T-Shirt Red" and "T-Shirt Blue") as separate top-level
 * products. This is a PATCH of `parentId`, not a new product: the row keeps
 * its id, so its SKU, stock, order history and vending assignments all carry
 * over untouched. The API (and a DB trigger behind it) enforces the same
 * rules `AddVariationDialog` relies on for a fresh variation — same type,
 * parent not itself a variation, parent active if this product is — so the
 * candidate list here is filtered to match, and only rows with no
 * variations of their own get the convert action at all.
 *
 * The same dialog moves a product that is already a variation to a different
 * listing (`product.parentId` set): the PATCH and its rules are identical, the
 * current listing is just left out of the candidates and the copy changes.
 */
function ChangeParentDialog({
  product,
  currentParent,
  onClose,
}: {
  product: ProductRow | null
  currentParent?: ProductRow | null
  onClose: () => void
}) {
  const isMove = product?.parentId != null
  const toast = useToast()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [selectedParent, setSelectedParent] = useState<ProductRow | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(search), 200)
    return () => clearTimeout(timer)
  }, [search])

  const parentFilters = {
    q: debouncedQ || undefined,
    type: product?.type,
    grouped: true as const,
    isActive: product?.isActive ? (true as const) : undefined,
    sort: 'name' as const,
    order: 'asc' as const,
    page: 1,
    pageSize: 50,
  }
  const { data: candidates, isFetching: searching } = useQuery({
    queryKey: queryKeys.products.list(parentFilters),
    queryFn: () => api.get<{ items: ProductRow[] }>('/products', { query: parentFilters }),
    enabled: product !== null,
  })
  // An archived listing can never take a new (or reattached) variation — the
  // API refuses it — so it isn't offered as a candidate here either. When
  // moving, the listing it already belongs to is no destination either.
  //
  // Listings now nest, so every node of the returned trees is a candidate as
  // long as the product — with whatever hangs under it — still fits inside
  // the three-level cap beneath it. A product can't go under its own
  // descendants. The server and the database re-check all of it.
  const productHeight = product ? treeHeight(product) : 0
  const ownIds = new Set<string>()
  const collectOwn = (p: ProductRow) => {
    ownIds.add(p.id)
    p.variations.forEach(collectOwn)
  }
  if (product) collectOwn(product)
  const needle = debouncedQ.trim().toLowerCase()
  const eligibleParents: Array<ProductRow & { depth: number }> = []
  const walkCandidates = (nodes: ProductRow[], depth: number) => {
    for (const c of nodes) {
      const matches =
        !needle || c.name.toLowerCase().includes(needle) || c.sku.toLowerCase().includes(needle)
      if (
        matches &&
        !ownIds.has(c.id) &&
        c.id !== product?.parentId &&
        !c.isArchived &&
        c.type === product?.type &&
        (c.isActive || !product?.isActive) &&
        depth + productHeight < MAX_PRODUCT_LEVELS - 1
      ) {
        eligibleParents.push({ ...c, depth })
      }
      walkCandidates(c.variations, depth + 1)
    }
  }
  walkCandidates(candidates?.items ?? [], 0)

  const convert = useMutation({
    mutationFn: () => api.patch(`/products/${product?.id}`, { parentId: selectedParent?.id }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      toast.success(
        isMove
          ? `${product?.name} moved to ${selectedParent?.name}`
          : `${product?.name} is now a variation of ${selectedParent?.name}`,
      )
      onClose()
    },
    onError: (error) =>
      toast.error(
        isMove ? 'Could not move the variation' : 'Could not convert the product',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={product !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isMove
              ? `Move ${product?.name} to another product`
              : `Make ${product?.name} a variation`}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {isMove && currentParent ? (
            <p className="text-sm text-foreground-muted">
              Currently a variation of {currentParent.name}.
              {currentParent.variations.length === 1
                ? ` ${currentParent.name} has no other variations — after the move it becomes a standalone product again.`
                : ''}
            </p>
          ) : null}
          <Field
            label={isMove ? 'New parent product' : 'Parent product'}
            hint={`Only ${product ? TYPE_LABEL[product.type] : ''} listings can be the parent.`}
          >
            {(props) => (
              <div className="relative">
                <Search
                  className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-foreground-subtle"
                  aria-hidden="true"
                />
                <Input
                  {...props}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search products…"
                  className="ps-8"
                />
              </div>
            )}
          </Field>
          <div className="max-h-64 overflow-y-auto rounded-md border border-border">
            {searching ? (
              <p className="p-2 text-sm text-foreground-muted">Searching…</p>
            ) : eligibleParents.length === 0 ? (
              <p className="p-2 text-sm text-foreground-muted">
                No matching listing found — it must be the same kind of product
                {product?.isActive ? ' and active' : ''}.
              </p>
            ) : (
              eligibleParents.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedParent(c)}
                  className={`flex w-full items-center justify-between gap-2 px-2 py-1.5 text-start text-sm hover:bg-surface-sunken ${
                    DEPTH_INDENT[c.depth] ?? ''
                  } ${c.id === selectedParent?.id ? 'bg-surface-sunken' : ''}`}
                >
                  <span>{c.name}</span>
                  <span className="shrink-0 font-identifier text-xs text-foreground-muted">
                    {c.sku}
                  </span>
                </button>
              ))
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!selectedParent}
            loading={convert.isPending}
            onClick={() => convert.mutate()}
          >
            {isMove ? 'Move' : 'Convert'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Editing a product.
 *
 * The type can change here, but only on a listing or standalone product: inventory
 * rows, order lines and the availability query all branch on it, so the API
 * refuses while stock or tracked-unit history exists and moves the variations
 * along with the listing. A variation shows its type read-only. Everything
 * else — including price — is fair game, and past orders are unaffected
 * because each line snapshots what it was sold for.
 */
/** Read-only summary opened by clicking a product row — for reading the
 *  description without dropping into the Edit dialog's editing workflow. */
function ProductQuickViewDialog({
  product,
  onClose,
}: {
  product: TableRow | null
  onClose: () => void
}) {
  return (
    <Dialog open={product !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              {product?.name}
              {product?.isArchived ? (
                <Badge tone="neutral">Archived</Badge>
              ) : product?.isActive === false ? (
                <Badge tone="neutral">Inactive</Badge>
              ) : null}
              {product?.isInternal ? <Badge tone="warning">Internal</Badge> : null}
              {product && product.salesChannel !== 'all' ? (
                <Badge tone="neutral">{SALES_CHANNEL_BADGE[product.salesChannel]}</Badge>
              ) : null}
            </div>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {product ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                {product.imageUrl ? (
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-border bg-surface-sunken">
                    <img src={product.imageUrl} alt="" className="h-full w-full object-cover" />
                  </div>
                ) : null}
                <div className="grid flex-1 grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div>
                    <p className="text-xs text-foreground-muted">SKU</p>
                    <p className="font-identifier">{product.sku}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-muted">Type</p>
                    <Badge tone={isUnitTracked(product.type) ? 'primary' : 'neutral'}>
                      {TYPE_LABEL[product.type]}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-muted">Category</p>
                    <p>{product.category ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-muted">Price</p>
                    {renderPrice(product)}
                  </div>
                  {product.isVariation && Object.keys(product.attributes).length > 0 ? (
                    <div className="col-span-2">
                      <p className="text-xs text-foreground-muted">Attributes</p>
                      <p>
                        {Object.entries(product.attributes)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(' · ')}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
              <div>
                <p className="mb-1 text-xs text-foreground-muted">Description</p>
                {product.description ? (
                  // Sanitized server-side (see modules/catalog/description.ts)
                  // before it ever reaches the database, same guarantee the
                  // workspace's posts and the wiki's pages get.
                  <div
                    className="rich-text text-sm"
                    dangerouslySetInnerHTML={{ __html: product.description }}
                  />
                ) : (
                  <p className="text-sm text-foreground-subtle">This product has no description.</p>
                )}
              </div>
            </div>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EditProductDialog({
  product,
  onClose,
}: {
  product: ProductRow | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const allCategoryOptions = useCategoryOptions()
  const [type, setType] = useState<ProductType>('stock')
  const isVariation = !!product?.parentId
  // A variation's type is its listing's, so only a listing or standalone
  // product can be retyped from here.
  const typeChanged = !!product && !isVariation && type !== product.type
  const effectiveType = isVariation ? product?.type : type
  // A category pinned to a different type is not an offer worth making —
  // except the product's own current category while the type is unchanged,
  // which has to stay choosable (and just sit there unchanged) even if it
  // predates this rule, the way the legacy Chocolates/Accessories row does.
  const categoryOptions = allCategoryOptions.filter(
    (c) =>
      !c.allowedType ||
      c.allowedType === effectiveType ||
      (!typeChanged && c.name === product?.category),
  )
  const [name, setName] = useState('')
  const [sku, setSku] = useState('')
  const [priceDollars, setPriceDollars] = useState('')
  const [costDollars, setCostDollars] = useState('')
  const [salePriceDollars, setSalePriceDollars] = useState('')
  const [description, setDescription] = useState<string | null>(null)
  const [category, setCategory] = useState('')
  const [taxCategoryId, setTaxCategoryId] = useState<string | null>(null)
  const [brand, setBrand] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [galleryImageUrls, setGalleryImageUrls] = useState<string[]>([])
  const [manufacturerSku, setManufacturerSku] = useState('')
  const [sourceUrl, setSourceUrl] = useState('')
  const [reorderPoint, setReorderPoint] = useState('')
  const [sortOrder, setSortOrder] = useState('100')
  const [isInternal, setIsInternal] = useState(false)
  const [salesChannel, setSalesChannel] = useState<ProductSalesChannel>('all')
  const [skuLocked, setSkuLocked] = useState(false)
  const [attributes, setAttributes] = useState<Record<string, string | number | boolean>>({})
  const [compatibleDevices, setCompatibleDevices] = useState<string[]>([])

  // Reset the form whenever a different product is opened; without the key on
  // `product.id` the previous product's values would linger.
  useEffect(() => {
    if (!product) return
    setName(product.name)
    setType(product.type)
    setSku(product.sku)
    setPriceDollars((product.priceCents / 100).toFixed(2))
    setCostDollars(product.costCents === null ? '' : (product.costCents / 100).toFixed(2))
    setSalePriceDollars(
      product.salePriceCents === null ? '' : (product.salePriceCents / 100).toFixed(2),
    )
    setDescription(product.description ?? null)
    setCategory(product.category ?? '')
    setTaxCategoryId(product.taxCategoryId)
    setBrand(product.brand ?? '')
    setImageUrl(product.imageUrl ?? '')
    setGalleryImageUrls(product.galleryImageUrls ?? [])
    setManufacturerSku(product.manufacturerSku ?? '')
    setSourceUrl(product.sourceUrl ?? '')
    setReorderPoint(product.reorderPoint === null ? '' : String(product.reorderPoint))
    setSortOrder(String(product.sortOrder))
    setIsInternal(product.isInternal)
    setSalesChannel(product.salesChannel ?? 'all')
    setSkuLocked(product.skuLocked)
    setAttributes(product.attributes ?? {})
    setCompatibleDevices(product.compatibleDevices ?? [])
  }, [product])

  const galleryChanged =
    JSON.stringify(galleryImageUrls) !== JSON.stringify(product?.galleryImageUrls ?? [])
  const devicesChanged =
    JSON.stringify(compatibleDevices) !== JSON.stringify(product?.compatibleDevices ?? [])

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/products/${product?.id}`, {
        name,
        ...(isVariation ? {} : { type }),
        sku: sku.trim(),
        priceCents: Math.round(Number(priceDollars || 0) * 100),
        costCents: costDollars.trim() === '' ? null : Math.round(Number(costDollars) * 100),
        salePriceCents:
          salePriceDollars.trim() === '' ? null : Math.round(Number(salePriceDollars) * 100),
        description,
        category: category.trim() || null,
        taxCategoryId,
        brand: brand.trim() || null,
        imageUrl: imageUrl.trim() || null,
        // Only when it changed, so an unrelated save never rewrites the
        // gallery (or undoes someone else's edit to it).
        ...(galleryChanged ? { galleryImageUrls } : {}),
        // Same rule: `[]` is a real change (clears every tag) and is sent.
        ...(devicesChanged ? { compatibleDevices } : {}),
        manufacturerSku: manufacturerSku.trim() || null,
        sourceUrl: sourceUrl.trim() || null,
        sortOrder: Number(sortOrder),
        isInternal,
        salesChannel,
        skuLocked,
        // Sent unconditionally (not just when non-empty) so clearing every
        // attribute actually persists as an empty record instead of leaving
        // the last saved set in place.
        attributes,
        // Only counted stock has a reorder point; the API rejects one anywhere
        // else, so it is not sent rather than sent as null and argued about.
        ...(effectiveType === 'stock'
          ? { reorderPoint: reorderPoint.trim() === '' ? null : Number(reorderPoint) }
          : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      toast.success('Saved')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={product !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {product?.name}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Name" required>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>

          <Field
            label="SKU"
            required
            hint={
              skuLocked
                ? 'Locked — turn off "Lock SKU" below to change it.'
                : "Unique matching key, also the product's shop page address — old links and CSVs using the previous SKU will stop matching."
            }
          >
            {(props) => (
              <div className="flex gap-2">
                <Input
                  {...props}
                  identifier
                  value={sku}
                  disabled={skuLocked}
                  onChange={(e) => setSku(e.target.value)}
                />
                {skuLocked ? null : (
                  <GenerateSkuButton
                    name={name}
                    parentId={product?.parentId ?? undefined}
                    onGenerated={setSku}
                  />
                )}
              </div>
            )}
          </Field>

          <Field
            label="Lock SKU"
            hint="Prevents the SKU from being edited until an admin unlocks it."
          >
            {(props) => <Switch {...props} checked={skuLocked} onCheckedChange={setSkuLocked} />}
          </Field>

          <Field
            label="Price"
            required
            hint="In dollars. Past orders keep the price they were sold at."
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                step="0.01"
                min="0"
                value={priceDollars}
                onChange={(e) => setPriceDollars(e.target.value)}
              />
            )}
          </Field>

          <Field
            label="Cost"
            hint="Optional. In dollars — what you pay, not the sale price. Feeds the cost of goods sold report."
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                step="0.01"
                min="0"
                value={costDollars}
                onChange={(e) => setCostDollars(e.target.value)}
              />
            )}
          </Field>

          {effectiveType !== 'gift_card' && (
            <Field label="Sale price" hint="Optional. Leave blank to sell at the regular price.">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  step="0.01"
                  min="0"
                  value={salePriceDollars}
                  onChange={(e) => setSalePriceDollars(e.target.value)}
                />
              )}
            </Field>
          )}

          <Field
            label="Order"
            hint="Low numbers come first on the shop page; for a variation, in its picker."
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min={0}
                max={9999}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            )}
          </Field>

          {isVariation ? (
            <p className="font-identifier text-2xs text-foreground-muted">
              {product ? TYPE_LABEL[product.type] : ''} — same as its listing. Change the type on
              the parent product.
            </p>
          ) : (
            <Field
              label="Type"
              hint={
                typeChanged && product
                  ? `Was ${TYPE_LABEL[product.type]}. Saving changes all of its variations too, and any stock on hand has to be removed first.`
                  : undefined
              }
            >
              {(props) => (
                <Select
                  value={type}
                  onValueChange={(value) => {
                    const next = value as ProductType
                    setType(next)
                    // A category pinned to another type would be refused on save.
                    const current = allCategoryOptions.find((c) => c.name === category)
                    if (current?.allowedType && current.allowedType !== next) setCategory('')
                  }}
                >
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="serialized">Phone (tracked by IMEI)</SelectItem>
                    <SelectItem value="stock">
                      Counted stock (accessories, or phones not tracked by IMEI)
                    </SelectItem>
                    <SelectItem value="plan">Service plan (recurring)</SelectItem>
                    <SelectItem value="service">Service (labour, one-off)</SelectItem>
                    <SelectItem value="sim">SIM card (tracked by ICCID)</SelectItem>
                    <SelectItem value="gift_card" disabled={product?.type !== 'gift_card'}>
                      Gift card (stored value)
                    </SelectItem>
                    <SelectItem value="digital_license">
                      Digital license (tracked by license number)
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              {(props) => (
                <Select
                  value={category || UNCATEGORISED}
                  onValueChange={(value) => setCategory(value === UNCATEGORISED ? '' : value)}
                >
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCATEGORISED}>No category</SelectItem>
                    {categoryOptions.map((c) => (
                      <SelectItem key={c.id} value={c.name}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Brand">
              {(props) => (
                <Input {...props} value={brand} onChange={(e) => setBrand(e.target.value)} />
              )}
            </Field>
          </div>

          <TaxCategoryField value={taxCategoryId} onChange={setTaxCategoryId} />

          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Manufacturer SKU"
              hint="The manufacturer's own part number. Admin-only — never shown to customers."
            >
              {(props) => (
                <Input
                  {...props}
                  value={manufacturerSku}
                  onChange={(e) => setManufacturerSku(e.target.value)}
                />
              )}
            </Field>
            <Field
              label="Source link"
              hint="Where to view or order this from the supplier. Admin-only — never shown to customers."
            >
              {(props) => (
                <Input
                  {...props}
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                />
              )}
            </Field>
          </div>

          <ProductImagesEditor
            imageUrl={imageUrl.trim() || null}
            galleryImageUrls={galleryImageUrls}
            onChange={(next) => {
              setImageUrl(next.imageUrl ?? '')
              setGalleryImageUrls(next.galleryImageUrls)
            }}
            aiHints={{ product: name, brand }}
          />

          {effectiveType === 'stock' ? (
            <Field
              label="Reorder point"
              hint="Low when total available across locations drops to this many or below. Leave blank to use the store-wide default from Settings."
            >
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min="0"
                  value={reorderPoint}
                  onChange={(e) => setReorderPoint(e.target.value)}
                />
              )}
            </Field>
          ) : null}

          <Field
            label="Shop use only"
            hint={
              product?.parentId
                ? 'Tracked in stock but never offered for sale. A variation cannot be made sellable while its listing is shop-use only.'
                : 'Tracked in stock but never offered for sale on the storefront, kiosk, or a vending machine.'
            }
          >
            {(props) => <Switch {...props} checked={isInternal} onCheckedChange={setIsInternal} />}
          </Field>

          <Field
            label="Where it's sold"
            hint={
              isVariation
                ? 'Follows its listing — change it on the parent product.'
                : 'Website, kiosk and vending machines, or both. Variations follow this listing.'
            }
          >
            {(props) => (
              <SalesChannelSelect
                props={props}
                value={salesChannel}
                onChange={setSalesChannel}
                disabled={isVariation}
              />
            )}
          </Field>

          <PlanBillingSection productId={product?.id} type={type} />

          <Field label="Description">
            {(props) => (
              <RichTextEditor
                {...props}
                // Tiptap only reads its initial content at mount, unlike a
                // plain controlled input — remount on the product id so
                // switching which product is being edited doesn't leave the
                // previous one's description showing.
                key={product?.id}
                value={description}
                onChange={setDescription}
                aiAssist={{
                  fieldLabel: 'product description',
                  hints: { Product: name, Brand: brand, Category: category },
                  imageId: imageIdFromUrl(imageUrl),
                  maxLength: 4000,
                }}
              />
            )}
          </Field>

          <Field label="Attributes" hint="Free-form details like color or storage size.">
            {() => (
              <AttributesEditor key={product?.id} value={attributes} onChange={setAttributes} />
            )}
          </Field>

          <Field
            label="Compatible devices"
            hint="Phones or models this fits (for cases, chargers, screen protectors…). Lets customers and staff search by device."
          >
            {() => (
              <CompatibleDevicesEditor
                key={product?.id}
                value={compatibleDevices}
                onChange={setCompatibleDevices}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

type BulkField = 'price' | 'category' | 'taxCategory' | 'active' | 'salesChannel'

/** One shared value applied to every selected product — exactly one field
 *  per action, so a blank input can never silently overwrite the others. */
function BulkEditDialog({
  open,
  onOpenChange,
  count,
  pending,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  count: number
  pending: boolean
  onSubmit: (set: {
    priceCents?: number
    category?: string | null
    taxCategoryId?: string | null
    isActive?: boolean
    salesChannel?: ProductSalesChannel
  }) => void
}) {
  const categoryOptions = useCategoryOptions()
  const taxCategoryOptions = useTaxCategoryOptions()
  const [field, setField] = useState<BulkField>('price')
  const [priceDollars, setPriceDollars] = useState('')
  const [category, setCategory] = useState(UNCATEGORISED)
  const [taxCategoryId, setTaxCategoryId] = useState(NON_TAXABLE)
  const [active, setActive] = useState<'true' | 'false'>('true')
  const [salesChannel, setSalesChannel] = useState<ProductSalesChannel>('all')

  // Reset every time the dialog is (re)opened — otherwise a value typed for
  // one selection lingers into the next.
  useEffect(() => {
    if (!open) return
    setField('price')
    setPriceDollars('')
    setCategory(UNCATEGORISED)
    setTaxCategoryId(NON_TAXABLE)
    setActive('true')
    setSalesChannel('all')
  }, [open])

  const priceValid = priceDollars.trim() !== '' && Number(priceDollars) >= 0
  const canSubmit = field !== 'price' || priceValid

  function submit() {
    if (field === 'price') {
      if (!priceValid) return
      onSubmit({ priceCents: Math.round(Number(priceDollars) * 100) })
    } else if (field === 'category') {
      onSubmit({ category: category === UNCATEGORISED ? null : category })
    } else if (field === 'taxCategory') {
      onSubmit({ taxCategoryId: taxCategoryId === NON_TAXABLE ? null : taxCategoryId })
    } else if (field === 'salesChannel') {
      onSubmit({ salesChannel })
    } else {
      onSubmit({ isActive: active === 'true' })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Edit {count} product{count === 1 ? '' : 's'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Field to change">
            {(props) => (
              <Select value={field} onValueChange={(value) => setField(value as BulkField)}>
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="price">Price</SelectItem>
                  <SelectItem value="category">Category</SelectItem>
                  <SelectItem value="taxCategory">Sales tax</SelectItem>
                  <SelectItem value="active">Active status</SelectItem>
                  <SelectItem value="salesChannel">Where it's sold</SelectItem>
                </SelectContent>
              </Select>
            )}
          </Field>

          {field === 'price' ? (
            <Field label="Price" required hint="In dollars. Applied to all selected products.">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  step="0.01"
                  min="0"
                  value={priceDollars}
                  onChange={(e) => setPriceDollars(e.target.value)}
                />
              )}
            </Field>
          ) : null}

          {field === 'category' ? (
            <Field label="Category">
              {(props) => (
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNCATEGORISED}>No category</SelectItem>
                    {categoryOptions.map((c) => (
                      <SelectItem key={c.id} value={c.name}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          ) : null}

          {field === 'salesChannel' ? (
            <Field
              label="Where it's sold"
              hint="A variation follows its listing, so select the listing to move its variations."
            >
              {(props) => (
                <SalesChannelSelect props={props} value={salesChannel} onChange={setSalesChannel} />
              )}
            </Field>
          ) : null}

          {field === 'active' ? (
            <Field label="Active status">
              {(props) => (
                <Select value={active} onValueChange={(value) => setActive(value as typeof active)}>
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={pending} disabled={!canSubmit} onClick={submit}>
            Apply to {count} product{count === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
